<template>
  <main class="fts-portal-launch">
    <!-- The debug launch's consent (docs/integration-portal-claude-design-proposal.md §3.4). -->
    <form
      class="fts-portal-launch-card fts-portal-debug"
      aria-labelledby="fts-portal-debug-title"
      v-if="debug.state === 'ask'"
      @submit.prevent="allowDebug"
    >
      <div class="fts-portal-launch-mark">
        <font-awesome-icon :icon="['fas', 'laptop']" />
      </div>
      <h1 id="fts-portal-debug-title">Debug a portal on this computer?</h1>
      <p>
        Your Inistate session for <strong>{{ debug.workspaceName }}</strong> will go to
        <code class="fts-portal-debug-origin">{{ debug.origin }}</code> on this computer for
        <strong>{{ hoursText(debug.hours) }}</strong>. Anything running there can read and submit as you in
        this workspace.
      </p>
      <p class="fts-portal-debug-activity" v-if="debug.context">
        It opens as an activity of this workspace{{ debug.context.entryId ? ", on one entry" : "" }}, as a member&rsquo;s click would.
      </p>
      <fieldset class="fts-portal-debug-hours">
        <legend>For how long</legend>
        <label v-for="h in debugHours" :key="h" class="fts-portal-debug-hour" :class="{ 'is-on': debug.hours === h }">
          <input type="radio" name="fts-portal-debug-hours" :value="h" v-model="debug.hours" />
          <span>{{ h }} h</span>
        </label>
      </fieldset>
      <div class="fts-portal-launch-actions">
        <button type="button" class="fts-portal-launch-btn" @click="cancelDebug">Cancel</button>
        <button type="submit" class="fts-portal-launch-btn is-primary">Allow</button>
      </div>
    </form>

    <div class="fts-portal-launch-card" role="status" v-else-if="debug.state === 'cancelled'">
      <div class="fts-portal-launch-mark">
        <font-awesome-icon :icon="['fas', 'laptop']" />
      </div>
      <h1>Nothing was shared</h1>
      <p>Your Inistate session did not go to {{ debug.origin || "this computer" }}. You can close this tab.</p>
      <div class="fts-portal-launch-actions">
        <a href="#/dashboard" class="fts-portal-launch-btn is-primary">Back to Inistate</a>
      </div>
    </div>

    <div class="fts-portal-launch-card" role="status" aria-live="polite" v-else-if="!failed">
      <div class="fts-portal-launch-mark">
        <font-awesome-icon v-if="icon.fa" :icon="icon.fa" />
        <span v-else aria-hidden="true">{{ icon.emoji }}</span>
      </div>
      <h1>{{ openingTitle }}</h1>
      <p>{{ isDebug ? "Sending your session to " + debug.origin + " on this computer." : "Signing you in with your own Inistate access." }}</p>
      <div class="fts-portal-launch-bar" aria-hidden="true"><div></div></div>
    </div>

    <div class="fts-portal-launch-card" role="alert" v-else>
      <div class="fts-portal-launch-mark is-warn">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
      </div>
      <h1>{{ failedTitle }}</h1>
      <p>{{ message }}</p>
      <div class="fts-portal-launch-actions">
        <button type="button" class="fts-portal-launch-btn" v-if="retryable" @click="retry">Try again</button>
        <a href="#/dashboard" class="fts-portal-launch-btn is-primary">Back to Inistate</a>
      </div>
    </div>
  </main>
</template>

<script>
/**
 * The bounce page behind a portal link: /#/portal/{id} (docs/integration-portal-link-proposal.md
 * §4.4 in the SVN repo). It is the app itself, in the new tab the side menu opened, with no shell.
 * It asks the server for the launch address and replaces itself with it, so it never stays in the
 * tab's history. The address carries a single-use code in its #fragment that the portal page
 * exchanges itself: this page never reads or keeps a portal's tokens.
 *
 * The same route is the portal's permanent link. Signed out, the router sends it through the login
 * page and back (portalReturn.js); a launch answered 401 that ends in a sign-out comes back too.
 *
 * An activity set to open a portal page (docs/integration-portal-claude-design-proposal.md §3.6)
 * comes here with ?module=&activity=&entry=, and the launch carries that context. The id `debug` is
 * reserved for the debug launch (§3.4): /#/portal/debug?ws=&port= asks the admin first, then sends
 * their own session to http://localhost:{port}/ on their own computer, for a few hours.
 */
import LocalStorageService from "../store/LocalStorageService";
import webAppConfig from "../commons/webApp.config";
import { launchPortal, debugLaunch, currentWorkspaceId } from "./portalApi";
import { portalIcon } from "./portalNav";
import { rememberPortalReturn, forgetPortalReturn } from "./portalReturn";

export const DEBUG_PORTAL_ID = "debug";
export const DEBUG_HOURS = Object.freeze([1, 2, 4, 8]);
export const DEBUG_DEFAULT_HOURS = 2;

/** The workspace the shells cached, when this browser has opened it before. */
function cachedWorkspace(workspaceId) {
  if (!workspaceId) return null;
  try {
    return LocalStorageService.getWorkspace(webAppConfig.baseServer, workspaceId) || null;
  } catch (err) {
    return null;
  }
}

/** The name and icon from the workspace the shells cached, when this browser has opened it before. */
function cachedPortal(id, workspaceId) {
  const workspace = cachedWorkspace(workspaceId);
  const list = workspace && Array.isArray(workspace.portals) ? workspace.portals : [];
  return list.find((p) => p && String(p.id) === String(id)) || null;
}

/** Only an https address is followed (the server only builds those): anything else is refused rather than run. */
export function isOpenableAddress(url) {
  return typeof url === "string" && /^https:\/\/[^\s]+$/i.test(url);
}

/**
 * A debug launch is followed only to http://localhost on the port the admin allowed, at its root,
 * with nothing after it but the #fragment that carries the code. The server only ever builds that
 * address (§3.8): never 127.0.0.1, a LAN address or any other name.
 */
export function isDebugAddress(url, port) {
  const p = String(port == null ? "" : port);
  if (typeof url !== "string" || !/^\d{4,5}$/.test(p)) return false;
  const match = /^http:\/\/localhost:(\d{1,5})\/(#\S*)?$/.exec(url);
  return Boolean(match) && match[1] === p;
}

/** A port a debug link may name: a whole number from 1024 to 65535, else null. */
export function debugPort(value) {
  const text = String(value == null ? "" : value).trim();
  if (!/^\d{4,5}$/.test(text)) return null;
  const port = Number(text);
  return port >= 1024 && port <= 65535 ? port : null;
}

function queryValue(query, key) {
  const value = query ? query[key] : null;
  return value == null ? "" : String(Array.isArray(value) ? value[0] : value).trim();
}

/**
 * The activity a link opens the portal as, from ?module=&activity=&entry=:
 * { context: { moduleId, activityId, entryId? } }, { context: null } for a menu launch, or
 * { invalid: true } for a link that names half of one or something that cannot be an id.
 */
export function linkContext(query) {
  const module = queryValue(query, "module");
  const activity = queryValue(query, "activity");
  const entry = queryValue(query, "entry");
  if (!module && !activity && !entry) return { context: null };
  if (!/^\d{1,10}$/.test(module) || !/^[A-Za-z0-9_-]{1,64}$/.test(activity)) return { invalid: true };
  if (entry && !/^[A-Za-z0-9_-]{1,64}$/.test(entry)) return { invalid: true };
  const context = { moduleId: Number(module), activityId: activity };
  if (entry) context.entryId = entry;
  return { context };
}

/** What a refused launch says: the server's own words, else one by status. */
export function launchFailureMessage(err) {
  const response = err && err.response;
  const data = response && response.data;
  if (data && typeof data === "object" && data.message) return data.message;
  const status = response && response.status;
  if (!response) return "Inistate could not be reached. Check your connection and try again.";
  if (status === 404) return "This portal is switched off or no longer exists.";
  if (status === 403) return "You do not have access to this portal.";
  if (status === 401) return "Your sign-in has ended. Sign in to Inistate again to open this portal.";
  return "This portal could not be opened right now.";
}

const DEBUG_NOT_ADMIN = "Only an admin of this workspace can debug a portal on this computer.";

/**
 * Plain words for a refused debug launch, by the contract's codes (§3.8), for an answer that came
 * without the server's own message (a proxy's error page, an older server).
 */
const DEBUG_REFUSALS = Object.freeze({
  not_enabled: "Debugging portals is switched off on this Inistate server.",
  admin_required: DEBUG_NOT_ADMIN,
  not_admin: DEBUG_NOT_ADMIN,
  not_a_member: "You are not a member of this workspace, so a portal cannot be debugged in it.",
  not_allowed_here: "Portals cannot be debugged in Run As, a demo or a guest session. Sign in to Inistate as yourself.",
  not_on_this_platform: "Debugging a portal works only in a browser on your own computer.",
  invalid_request: "This debug link asks for a port or a length Inistate does not allow: a port from 1024 to 65535, for 1, 2, 4 or 8 hours.",
  invalid_context: "This debug link names an activity Inistate cannot open. Run /portal try again.",
  activity_not_portal: "The activity this debug link names is not in this workspace.",
  activity_not_allowed: "You cannot perform the activity this debug link names.",
});

/** What a refused debug launch says: the server's own words, else plain words by code, else by status. */
export function debugFailureMessage(err) {
  const response = err && err.response;
  if (!response) return "Inistate could not be reached. Check your connection and try again.";
  const data = response.data && typeof response.data === "object" ? response.data : {};
  const code = typeof data.error === "string" ? data.error : "";
  if (data.message) return data.message;
  if (DEBUG_REFUSALS[code]) return DEBUG_REFUSALS[code];
  const status = response.status;
  if (status === 401) return "Your sign-in has ended. Sign in to Inistate again, then open the debug link again.";
  if (status === 403) return DEBUG_NOT_ADMIN;
  if (status === 404) return DEBUG_REFUSALS.not_enabled;
  return "The debug session could not be started right now.";
}

function emptyDebug() {
  return { state: "", workspaceId: "", workspaceName: "", port: null, origin: "", hours: DEBUG_DEFAULT_HOURS, context: null };
}

export default {
  name: "PortalLaunch",
  metaInfo() {
    if (this.isDebug) return { title: "Debug a portal" };
    return { title: this.name ? "Opening " + this.name : "Opening portal" };
  },
  data() {
    return {
      name: "",
      icon: portalIcon(null),
      failed: false,
      retryable: false,
      message: "",
      debug: emptyDebug(),
      debugHours: DEBUG_HOURS,
    };
  },
  computed: {
    portalId() {
      return String((this.$route && this.$route.params && this.$route.params.id) || "");
    },
    isDebug() {
      return this.portalId === DEBUG_PORTAL_ID;
    },
    openingTitle() {
      if (this.isDebug) return "Opening " + (this.debug.origin || "the page on this computer") + "…";
      return "Opening " + (this.name || "the portal") + "…";
    },
    failedTitle() {
      if (this.isDebug) return "The debug session did not start";
      return (this.name || "This portal") + " did not open";
    },
  },
  methods: {
    hoursText(hours) {
      return Number(hours) === 1 ? "1 hour" : hours + " hours";
    },
    query() {
      return (this.$route && this.$route.query) || {};
    },
    fail(message, retryable) {
      this.failed = true;
      this.retryable = Boolean(retryable);
      this.message = message;
    },
    start() {
      if (this.isDebug) this.askDebug();
      else this.launch();
    },
    retry() {
      if (this.isDebug) this.allowDebug();
      else this.launch();
    },

    // ---- the debug launch (§3.4) ----
    /** Reads the link and shows the card. Nothing is sent until Allow. */
    askDebug() {
      const query = this.query();
      this.failed = false;
      this.retryable = false;
      this.message = "";
      this.debug = emptyDebug();
      // The card names the workspace, so it must be the link's own: never whichever one the cookie holds.
      const workspaceId = String(query.ws || "");
      if (!/^\d{1,10}$/.test(workspaceId)) {
        this.fail("This debug link does not name a workspace. Run /portal try again.");
        return;
      }
      const port = debugPort(query.port);
      if (port == null) {
        this.fail("This debug link has no port Inistate can send a session to: it must be from 1024 to 65535. Run /portal try again.");
        return;
      }
      const link = linkContext(query);
      if (link.invalid) {
        this.fail(DEBUG_REFUSALS.invalid_context);
        return;
      }
      const workspace = cachedWorkspace(workspaceId);
      const workspaceName = workspace && workspace.name ? String(workspace.name) : "workspace " + workspaceId;
      this.debug = {
        state: "ask",
        workspaceId,
        workspaceName,
        port,
        origin: "http://localhost:" + port,
        hours: DEBUG_DEFAULT_HOURS,
        context: link.context,
      };
    },
    cancelDebug() {
      this.debug = { ...this.debug, state: "cancelled" };
    },
    async allowDebug() {
      const d = this.debug;
      if (!d.port || d.state === "opening") return;
      this.failed = false;
      this.retryable = false;
      this.message = "";
      this.debug = { ...d, state: "opening" };

      let url = null;
      try {
        const res = await debugLaunch({ port: d.port, hours: d.hours, context: d.context, workspaceId: d.workspaceId });
        url = res && res.data ? res.data.url : null;
        if (!isDebugAddress(url, d.port)) {
          throw { response: { status: 502, data: { message: "Inistate did not answer with the page on this computer to open." } } };
        }
      } catch (err) {
        if (this._isDestroyed) return;
        const status = err && err.response && err.response.status;
        this.debug = { ...this.debug, state: "" };
        this.fail(debugFailureMessage(err), !status || status >= 500);
        return;
      }

      if (this._isDestroyed) return;
      window.location.replace(url);
    },

    // ---- a portal link, or an activity that opens one ----
    async launch() {
      const id = this.portalId;
      const query = this.query();
      // The link names the portal's workspace (?ws=); the cookie is only the fallback for an older link.
      const fromLink = String(query.ws || "");
      const workspaceId = /^\d{1,10}$/.test(fromLink) ? fromLink : currentWorkspaceId();
      const known = cachedPortal(id, workspaceId);
      if (known) {
        this.name = String(known.name || "");
        this.icon = portalIcon(known.icon);
      }
      this.failed = false;
      this.retryable = false;
      this.message = "";

      const link = linkContext(query);
      if (link.invalid) {
        this.fail("This link names an activity Inistate cannot open. Open it again from Inistate.");
        return;
      }

      // Kept until the server answers: a 401 the shared client cannot refresh signs the person out,
      // and the login page then brings them back here rather than to the dashboard.
      rememberPortalReturn(id);

      let url = null;
      try {
        const res = link.context
          ? await launchPortal(id, workspaceId, link.context)
          : await launchPortal(id, workspaceId);
        url = res && res.data ? res.data.url : null;
        if (!isOpenableAddress(url)) {
          throw { response: { status: 502, data: { message: "The portal did not answer with an address to open." } } };
        }
      } catch (err) {
        const status = err && err.response && err.response.status;
        if (status !== 401) forgetPortalReturn();
        if (this._isDestroyed) return;
        this.failed = true;
        this.retryable = !status || status >= 500;
        this.message = launchFailureMessage(err);
        return;
      }

      forgetPortalReturn();
      if (this._isDestroyed) return;
      // replace(): this page does not stay in the tab's history.
      window.location.replace(url);
    },
  },
  watch: {
    // The same page reused for another portal (the address edited in place): launch that one.
    portalId(value, before) {
      if (value && value !== before) this.start();
    },
  },
  created() {
    this.start();
  },
};
</script>

<style lang="scss" scoped>
.fts-portal-launch {
  box-sizing: border-box;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  background: var(--inistate-color-neutral-50, #f5f6f7);
  color: var(--inistate-color-neutral-800, #424242);
  font-size: 0.875rem;
}

.fts-portal-launch-card {
  box-sizing: border-box;
  width: 100%;
  max-width: 420px;
  padding: 40px 44px;
  border: 1px solid var(--inistate-color-neutral-200, #e0e0e0);
  border-radius: 16px;
  background: var(--inistate-color-shade-white, #ffffff);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;

  h1 {
    margin: 0;
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 600;
    color: var(--inistate-color-neutral-900, #212121);
    overflow-wrap: anywhere;
  }

  p {
    margin: 0;
    line-height: 1.375rem;
    color: var(--inistate-color-neutral-700, #595959);
  }
}

.fts-portal-launch-mark {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background: var(--inistate-color-primary-50, #e2f0f5);
  color: var(--inistate-color-primary-700, #1e6f8b);

  &.is-warn {
    border-radius: 50%;
    background: var(--inistate-color-orange-50, #fff4e5);
    color: var(--inistate-color-orange-900, #8a3b0c);
  }
}

/* An indeterminate bar: the wait is short, and no number would be honest. */
.fts-portal-launch-bar {
  width: 100%;
  height: 4px;
  border-radius: 999px;
  overflow: hidden;
  background: var(--inistate-color-neutral-100, #eeeeee);

  div {
    width: 40%;
    height: 100%;
    border-radius: 999px;
    background: var(--inistate-color-primary-500, #2b9ec6);
    animation: fts-portal-launch-slide 1.2s ease-in-out infinite;
  }
}

@keyframes fts-portal-launch-slide {
  from { transform: translateX(-100%); }
  to { transform: translateX(250%); }
}

@media (prefers-reduced-motion: reduce) {
  .fts-portal-launch-bar div {
    animation: none;
    width: 100%;
    opacity: 0.5;
  }
}

/* ---- the debug card ---- */
.fts-portal-debug {
  max-width: 480px;

  p strong {
    font-weight: 600;
    color: var(--inistate-color-neutral-900, #212121);
  }
}

.fts-portal-debug-origin {
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--inistate-color-neutral-100, #eeeeee);
  color: var(--inistate-color-neutral-900, #212121);
  font-size: 0.8125rem;
  overflow-wrap: anywhere;
}

.fts-portal-debug-hours {
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;

  legend {
    width: 100%;
    margin-bottom: 8px;
    padding: 0;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--inistate-color-neutral-700, #595959);
  }
}

.fts-portal-debug-hour {
  position: relative;
  margin: 0;
  min-width: 56px;
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid var(--inistate-color-neutral-200, #e0e0e0);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }

  &.is-on {
    border-color: var(--inistate-color-primary-700, #1e6f8b);
    box-shadow: 0 0 0 1px var(--inistate-color-primary-700, #1e6f8b);
    background: var(--inistate-color-primary-50, #e2f0f5);
    color: var(--inistate-color-primary-700, #1e6f8b);
  }

  &:focus-within {
    outline: 2px solid var(--inistate-color-primary-500, #2b9ec6);
    outline-offset: 2px;
  }
}

.fts-portal-launch-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
}

.fts-portal-launch-btn {
  box-sizing: border-box;
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid var(--inistate-color-neutral-200, #e0e0e0);
  border-radius: 8px;
  background: var(--inistate-color-shade-white, #ffffff);
  color: var(--inistate-color-neutral-900, #212121);
  font: inherit;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: var(--inistate-color-neutral-50, #f5f6f7);
    text-decoration: none;
  }

  &.is-primary {
    border-color: var(--inistate-color-primary-700, #1e6f8b);
    background: var(--inistate-color-primary-700, #1e6f8b);
    color: var(--inistate-color-shade-white, #ffffff);

    &:hover {
      background: var(--inistate-color-primary-800, #164f63);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--inistate-color-primary-500, #2b9ec6);
    outline-offset: 2px;
  }
}
</style>
